import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeSitemap.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import ToggleSwitch from '../components/ToggleSwitch/ToggleSwitch'
import MicrositeSlider from '../components/MicrositeSlider/MicrositeSlider'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeCopyableUrl from '../components/MicrositeCopyableUrl/MicrositeCopyableUrl'
import MicrositeLastGeneratedBadge from '../components/MicrositeLastGeneratedBadge/MicrositeLastGeneratedBadge'

const FREQUENCY_OPTIONS = [
    { value: 'always', label: 'Always' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'never', label: 'Never' }
]

const EMPTY_FORM = {
    includeHomePage: true,
    includeBlogPosts: true,
    includeCustomPages: true,
    includeServices: true,
    defaultPriority: 0.5,
    defaultFrequency: 'weekly'
}

const MicrositeSitemap = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [toast, setToast] = useState(null)

    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)
    const [sitemapUrl, setSitemapUrl] = useState(null)
    const [lastGeneratedAt, setLastGeneratedAt] = useState(null)

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadSitemap = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetStoreSeo')
        if (res?.status === 'success') {
            const s = res.response?.sitemapConfig || {}
            const next = {
                includeHomePage: s.includeHomePage ?? true,
                includeBlogPosts: s.includeBlogPosts ?? true,
                includeCustomPages: s.includeCustomPages ?? true,
                includeServices: s.includeServices ?? true,
                defaultPriority: s.defaultPriority ?? 0.5,
                defaultFrequency: s.defaultFrequency || 'weekly'
            }
            setForm(next)
            setOriginal(next)
            setSitemapUrl(s.sitemapUrl || null)
            setLastGeneratedAt(s.lastGeneratedAt || null)
        } else {
            showToast('Could not load sitemap settings. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadSitemap()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    const handleSave = async () => {
        setSaving(true)
        const res = await post('micrositeUpdateStoreSitemapConfig', { ...form })
        setSaving(false)

        if (res?.status === 'success') {
            setOriginal(form)
            showToast('Sitemap settings saved successfully')
        } else {
            showToast('Could not save sitemap settings', 'error')
        }
    }

    const handleGenerate = async () => {
        setGenerating(true)
        const res = await post('micrositeGenerateSitemap')
        setGenerating(false)

        if (res?.status === 'success') {
            setSitemapUrl(res.response?.sitemapUrl || sitemapUrl)
            setLastGeneratedAt(res.response?.generatedAt || new Date().toISOString())
            showToast(`Sitemap generated with ${res.response?.urlCount ?? 0} URLs`)
        } else {
            showToast('Could not generate sitemap. Please try again.', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading sitemap settings…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Sitemap"
                backTo="/microsite/seo"
                backLabel="SEO settings"
                subtitle="Control what's included in your sitemap.xml file."
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
            />

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Your sitemap</h2>
                <MicrositeCopyableUrl url={sitemapUrl} />
                <MicrositeLastGeneratedBadge
                    lastGeneratedAt={lastGeneratedAt}
                    onRegenerate={handleGenerate}
                    loading={generating}
                />
            </section>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>What to include</h2>
                <p className={styles.groupDesc}>Choose which content types appear in your sitemap.</p>

                <ToggleSwitch
                    label="Home page"
                    checked={form.includeHomePage}
                    onChange={(v) => update('includeHomePage', v)}
                />
                <div className={styles.toggleSpacer} />
                <ToggleSwitch
                    label="Blog posts"
                    checked={form.includeBlogPosts}
                    onChange={(v) => update('includeBlogPosts', v)}
                />
                <div className={styles.toggleSpacer} />
                <ToggleSwitch
                    label="Custom pages"
                    checked={form.includeCustomPages}
                    onChange={(v) => update('includeCustomPages', v)}
                />
                <div className={styles.toggleSpacer} />
                <ToggleSwitch
                    label="Services"
                    checked={form.includeServices}
                    onChange={(v) => update('includeServices', v)}
                />
            </section>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Defaults</h2>
                <p className={styles.groupDesc}>Applied to pages without a specific SEO override.</p>

                <MicrositeSlider
                    label="Default priority"
                    value={form.defaultPriority}
                    onChange={(v) => update('defaultPriority', v)}
                />
                <SelectInput
                    label="Default change frequency"
                    value={form.defaultFrequency}
                    onChange={(v) => update('defaultFrequency', v)}
                    options={FREQUENCY_OPTIONS}
                />
            </section>
        </div>
    )
}

export default MicrositeSitemap